import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { installAuthRefresh } from '../lib/authRefresh';
import { installIdleWatcher, recordActivity, clearActivity, isIdleExpired, broadcastIdleLogout } from '../lib/idleTimeout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Live ref so the auth-refresh layer can read the latest auth state without re-installing.
  const userRef = useRef(user);
  userRef.current = user;
  const sessionExpiredShownRef = useRef(false);
  const idleLogoutShownRef = useRef(false);

  // Inactivity (idle) auto-logout: sign out after >4h with no real user interaction.
  // Background polling never resets the timer; the marker persists across browser close.
  const idleLogout = useCallback(async () => {
    if (idleLogoutShownRef.current || !userRef.current || typeof userRef.current !== 'object') return;
    idleLogoutShownRef.current = true;
    broadcastIdleLogout();
    clearActivity();
    try {
      await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch { /* ignore */ }
    setUser(false);
    toast.error('Signed out after 4 hours of inactivity. Please sign in again.', { duration: 6000 });
  }, []);

  useEffect(() => {
    const cleanup = installIdleWatcher({
      isLoggedIn: () => !!userRef.current && typeof userRef.current === 'object',
      onActivity: () => { idleLogoutShownRef.current = false; },
      onExpire: idleLogout,
    });
    return cleanup;
  }, [idleLogout]);

  // Install the silent access-token refresh (single-flight refresh + retry). Runs once.
  // Active users get seamless renewal; "session expired" only surfaces when the 7-day
  // refresh token is also dead AND the user was actually logged in.
  useEffect(() => {
    installAuthRefresh({
      isLoggedIn: () => !!userRef.current && typeof userRef.current === 'object',
      onSessionExpired: () => {
        if (sessionExpiredShownRef.current) return;
        sessionExpiredShownRef.current = true;
        setUser(false);
        toast.error('Your session has expired. Please sign in again.', { duration: 6000 });
      },
    });
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        // Returning after a long absence: if the idle window already elapsed, don't
        // restore the session — sign out instead.
        if (isIdleExpired()) {
          clearActivity();
          try { await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' }); } catch { /* ignore */ }
          setUser(false);
        } else {
          recordActivity(true); // fresh marker for this session
          setUser(data);
        }
      } else {
        setUser(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // One-time-per-session trial greeting. Shows as a toast (no layout shift) after
  // login/refresh, in whichever app (IntelliTix or BetMaker) the user lands on.
  useEffect(() => {
    if (!user || typeof user !== 'object') return;
    if (user.subscription_status !== 'trial') return;
    const ends = user.trial_ends_at || user.subscription_ends_at;
    if (!ends) return;
    if (sessionStorage.getItem('trialGreetShown')) return;
    const d = new Date(ends);
    if (isNaN(d.getTime())) return;
    const mmdd = String(d.getMonth() + 1).padStart(2, '0') + '/' + String(d.getDate()).padStart(2, '0');
    sessionStorage.setItem('trialGreetShown', '1');
    toast.success(`Enjoy your Free Trial until ${mmdd}`, { duration: 6000 });
  }, [user]);

  // Safe response reader that handles browser extension interference
  const safeReadResponse = async (response) => {
    try {
      // Try arrayBuffer first - most reliable, can't be intercepted
      const buffer = await response.arrayBuffer();
      const text = new TextDecoder().decode(buffer);
      return JSON.parse(text);
    } catch (e) {
      // Log the actual error for debugging
      console.error('safeReadResponse error:', e.name, e.message);
      // Body was already consumed (by monitoring tools, extensions, etc.)
      // Return a fallback based on response status
      if (!response.ok) {
        return { detail: `Request failed with status ${response.status}` };
      }
      throw new Error('BROWSER_INTERFERENCE');
    }
  };

  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    
    // Handle errors by status code - don't try to read body
    if (response.status === 401) {
      throw new Error('Invalid email or password');
    }
    if (response.status === 429) {
      throw new Error('Too many attempts. Please try again in 15 minutes.');
    }
    if (!response.ok) {
      throw new Error('Login failed. Please try again.');
    }
    
    // Success - fetch user data from /me endpoint instead of reading login response
    const meResponse = await fetch(`${API_URL}/api/auth/me`, {
      credentials: 'include',
    });
    
    if (!meResponse.ok) {
      throw new Error('Login succeeded but failed to load user data.');
    }
    
    const data = await meResponse.json();
    sessionExpiredShownRef.current = false;
    idleLogoutShownRef.current = false;
    recordActivity(true);
    setUser(data);
    return data;
  };

  const register = async (email, password, name, affid = null, termsAccepted = false, referralSource = null, referralDetail = null, turnstileToken = null, hpField = '') => {
    const body = { email, password, name, terms_accepted: termsAccepted };
    if (affid) body.affid = affid;
    if (referralSource) body.referral_source = referralSource;
    if (referralDetail) body.referral_source_detail = referralDetail;
    if (turnstileToken) body.turnstile_token = turnstileToken;
    body.hp_field = hpField || '';
    
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      
      const data = await safeReadResponse(response);
      
      if (!response.ok) {
        throw new Error(formatApiError(data.detail));
      }
      
      idleLogoutShownRef.current = false;
      recordActivity(true);
      setUser(data);
      return data;
    } catch (error) {
      if (error.message === 'BROWSER_INTERFERENCE') {
        throw new Error('Registration failed due to browser interference. Please try incognito mode.');
      }
      throw error;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    clearActivity();
    setUser(false);
  };

  const forgotPassword = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      const data = await safeReadResponse(response);
      
      if (!response.ok) {
        throw new Error(formatApiError(data.detail));
      }
      
      return data;
    } catch (error) {
      if (error.message === 'BROWSER_INTERFERENCE') {
        throw new Error('Request failed due to browser interference. Please try incognito mode.');
      }
      throw error;
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      
      const data = await safeReadResponse(response);
      
      if (!response.ok) {
        throw new Error(formatApiError(data.detail));
      }
      
      return data;
    } catch (error) {
      if (error.message === 'BROWSER_INTERFERENCE') {
        throw new Error('Request failed due to browser interference. Please try incognito mode.');
      }
      throw error;
    }
  };

  const refreshAuth = () => {
    return checkAuth();
  };

  // Check subscription access
  const hasActiveSubscription = () => {
    if (!user) return false;
    // Admins always have access
    if (user.role === 'admin') return true;
    // Free-access promo: IntelliTix is free for any logged-in account on Mon/Tue
    // (backend is the source of truth for the day, via free_access_day).
    if (user.free_access_day) return true;
    
    // Helper to compare dates (ignoring time) - allows full day access on expiry date
    const isDateValid = (dateStr) => {
      if (!dateStr) return false;
      const expiryDate = new Date(dateStr).toDateString();
      const today = new Date().toDateString();
      return new Date(expiryDate) >= new Date(today);
    };
    
    // For trial users, check trial_ends_at
    if (user.subscription_status === 'trial') {
      if (user.trial_ends_at) {
        return isDateValid(user.trial_ends_at);
      }
      // Fallback to subscription_ends_at if trial_ends_at not set
      if (user.subscription_ends_at) {
        return isDateValid(user.subscription_ends_at);
      }
      return true; // No date set = unlimited
    }
    
    // For active users, check subscription_ends_at
    if (user.subscription_status === 'active') {
      if (user.subscription_ends_at) {
        return isDateValid(user.subscription_ends_at);
      }
      return true; // No date set = unlimited
    }
    
    return false;
  };

  // BetMaker access: admins always; INCLUDED FREE during an active trial (any tier);
  // otherwise needs the betmaker_enabled flag (bundle subscribers / admin-granted).
  const hasBetmaker = () => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    // Free-access promo: IntelliTix Pro (BetMaker) is free for any logged-in
    // account on Mon/Tue free-access days.
    if (user.free_access_day) return true;
    if (user.betmaker_enabled) {
      // A day-pass entitlement only lasts while its access window is valid.
      if (user.subscription_plan === 'day_pass') return hasActiveSubscription();
      return true;
    }
    if (user.subscription_status === 'trial') {
      const ends = user.trial_ends_at || user.subscription_ends_at;
      if (!ends) return true;
      const expiryDate = new Date(new Date(ends).toDateString());
      const today = new Date(new Date().toDateString());
      return expiryDate >= today;
    }
    return false;
  };

  // Exacta Value access: admins always; otherwise the exacta_value_enabled flag.
  const hasExactaValue = () => {
    if (!user) return false;
    return user.role === 'admin' || !!user.exacta_value_enabled;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    refreshAuth,
    hasActiveSubscription,
    hasBetmaker,
    hasExactaValue,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper to format API error responses
function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default AuthContext;
